import type {
  ChangeEvent,
  ChangeEventHandler,
  HTMLAttributes,
  ReactNode,
} from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { CheckmarkIcon, MinusIcon } from '../../images/shapes';

export type CheckboxChangeFn = (
  e: ChangeEvent<HTMLInputElement>,
) => boolean | void | Promise<boolean | void>;

export interface CheckboxProps extends HTMLAttributes<HTMLInputElement> {
  /**
   * Checkbox id
   */
  id?: string;
  /**
   * Checkbox name
   */
  name?: string;
  /**
   * Checkbox value
   */
  checked?: boolean;
  /**
   * On checkbox change callback
   */
  onChange?: CheckboxChangeFn;
  /**
   * If checkbox is indeterminate
   */
  indeterminate?: boolean;
  /**
   * Checkbox label
   */
  label?: string | ReactNode;
  /**
   * If checkbox is disabled
   */
  disabled?: boolean;
  /**
   * Checkbox text that will inform about error
   */
  error?: string | string[];
  /**
   * If checkbox is circular
   */
  circular?: boolean;
  /**
   * Checkbox additional container classes
   */
  additionalContainerClasses?: string;
  /**
   * Checkbox additional label classes
   */
  additionalLabelClasses?: string;
  /**
   * Checkbox container additional label classes
   */
  additionalCheckboxContainerClasses?: string;
  /**
   * Checkbox additional classes
   */
  additionalCheckboxClasses?: string;
  /**
   * Checkbox additional text error classes
   */
  additionalErrorTextClasses?: string;
  /**
   * Checkbox test id
   */
  testId?: string;
}

const Checkbox = ({
  id = '',
  name = '',
  checked = false,
  label = '',
  onChange,
  onBlur,
  disabled = false,
  error = '',
  circular = false,
  indeterminate = false,
  additionalContainerClasses = '',
  additionalLabelClasses = '',
  additionalCheckboxContainerClasses = '',
  additionalCheckboxClasses = '',
  additionalErrorTextClasses = '',
  testId = '',
}: CheckboxProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const [currentChecked, setCurrentChecked] = useState(checked);

  useEffect(() => {
    setCurrentChecked(checked);
  }, [checked]);

  const onChangeLocal = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      const value = e.target.checked;
      setCurrentChecked(value);

      const result = await onChange?.(e);
      if (result != null) {
        setCurrentChecked(result);
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div
      className={twMerge('flex w-fit items-center', additionalContainerClasses)}
      {...getTestProps(testId, 'container')}
    >
      <div
        className={twMerge(
          'w-fit h-fit self-start relative',
          additionalCheckboxContainerClasses,
        )}
      >
        {!indeterminate && checked && (
          <CheckmarkIcon
            className="pointer-events-none absolute left-1/4 top-1/4 w-1/2 h-1/2 
                      text-white dark:text-slate-950"
          />
        )}
        {indeterminate && (
          <MinusIcon
            className="pointer-events-none absolute left-[15%] top-1/4 w-[70%] h-1/2 
                      text-white dark:text-slate-950"
          />
        )}
        <input
          ref={checkboxRef}
          name={name}
          id={id || name}
          className={twMerge(
            'flex items-center justify-center hover:cursor-pointer text-white appearance-none',
            'w-6 h-6 bg-transparent dark:bg-transparent checked:bg-blue dark:checked:bg-blue',
            'border border-blue dark:border-blue',
            indeterminate && 'bg-blue dark:bg-blue disabled:bg-slate-400',
            circular ? 'rounded-full' : 'rounded-sm',
            'disabled:cursor-not-allowed disabled:hover:cursor-not-allowed',
            'disabled:checked:bg-slate-400 disabled:border-slate-400',
            additionalCheckboxClasses,
          )}
          type="checkbox"
          checked={currentChecked}
          onChange={onChangeLocal}
          onBlur={onBlur}
          disabled={disabled}
          {...getTestProps(testId, 'checkbox')}
        />
      </div>

      <div className="flex flex-col">
        {label && (
          <label
            htmlFor={name}
            className={twMerge(
              'cursor-pointer text-left text-lg ml-1.5 dark:text-white',
              disabled && 'cursor-not-allowed hover:cursor-not-allowed',
              additionalLabelClasses,
            )}
            {...getTestProps(testId, 'label')}
          >
            {label}
          </label>
        )}

        {error && (
          <span
            className={twMerge(
              'text-red mt-1 mb-2 leading-none',
              additionalErrorTextClasses,
            )}
            {...getTestProps(testId, 'error')}
          >
            {error}
          </span>
        )}
      </div>
    </div>
  );
};

export default Checkbox;
